import React, { useRef, useState, useEffect } from "react";
import { Upload, RotateCw, Move, ZoomIn, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Live POD customizer — upload artwork, drag/resize/rotate over product mockup,
 * generate mockup preview and emit payload for cart.
 */
export default function PODCustomizer({ productId, podConfig, onChange }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [artworkUrl, setArtworkUrl] = useState(null);
  const [artworkFileId, setArtworkFileId] = useState(null);
  const [warning, setWarning] = useState(null);
  const [zone, setZone] = useState(podConfig?.placement_zones?.[0] || "front");
  const [customText, setCustomText] = useState("");

  // transform state (percent-based for responsive)
  const [pos, setPos] = useState({ x: 50, y: 50 }); // center %
  const [scale, setScale] = useState(40); // % of container width
  const [rotation, setRotation] = useState(0);

  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);

  const mockup = podConfig?.mockup_image || "https://static.prod-images.emergentagent.com/jobs/c297ebbe-aee1-49b0-ab19-6c889e96a046/images/d1e00e82f9a80bc7b2d1ac78dacbe15613f2d6ac433ba1fff1142106b3cd45f9.png";

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["png", "jpg", "jpeg", "svg", "pdf"].includes(ext)) {
      toast.error("Use PNG, JPG, SVG or PDF.");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("product_id", productId);
    try {
      const { data } = await api.post("/pod/artwork", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setArtworkFileId(data.file_id);
      setWarning(data.warning);
      // Local preview from blob (avoid re-fetching)
      setArtworkUrl(URL.createObjectURL(f));
      toast.success("Artwork uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const startDrag = (e) => {
    setDragging(true);
    const point = e.touches ? e.touches[0] : e;
    dragStart.current = { x: point.clientX, y: point.clientY, px: pos.x, py: pos.y };
  };
  const onDrag = (e) => {
    if (!dragging || !dragStart.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    const dx = ((point.clientX - dragStart.current.x) / rect.width) * 100;
    const dy = ((point.clientY - dragStart.current.y) / rect.height) * 100;
    setPos({ x: Math.max(10, Math.min(90, dragStart.current.px + dx)), y: Math.max(10, Math.min(90, dragStart.current.py + dy)) });
  };
  const endDrag = () => setDragging(false);

  useEffect(() => {
    const onUp = () => setDragging(false);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  // Emit payload + generate mockup preview when changed
  useEffect(() => {
    if (!artworkFileId) {
      onChange?.(null);
      return;
    }
    // Compose mockup on canvas
    const compose = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const W = 800, H = 800;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#f3f2f0";
      ctx.fillRect(0, 0, W, H);
      const baseImg = new Image();
      baseImg.crossOrigin = "anonymous";
      baseImg.onload = () => {
        ctx.drawImage(baseImg, 0, 0, W, H);
        const art = new Image();
        art.crossOrigin = "anonymous";
        art.onload = () => {
          const sw = (scale / 100) * W;
          const sh = sw * (art.height / art.width);
          ctx.save();
          ctx.translate((pos.x / 100) * W, (pos.y / 100) * H);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.drawImage(art, -sw / 2, -sh / 2, sw, sh);
          if (customText && podConfig.allow_custom_text) {
            ctx.fillStyle = "#1A1A18";
            ctx.font = "bold 28px Outfit, sans-serif";
            ctx.textAlign = "center";
            ctx.fillText(customText, 0, sh / 2 + 40);
          }
          ctx.restore();
          try {
            const dataUrl = canvas.toDataURL("image/png");
            onChange?.({
              artwork_file_id: artworkFileId,
              mockup_data_url: dataUrl, // sent to cart, persisted on order creation by backend
              zone,
              scale,
              rotation,
              position: pos,
              custom_text: customText || undefined,
              template_kind: podConfig.template_kind,
            });
          } catch (e) {
            // tainted canvas if cross-origin fails — still send minimal payload
            onChange?.({
              artwork_file_id: artworkFileId,
              zone, scale, rotation, position: pos,
              custom_text: customText || undefined,
              template_kind: podConfig.template_kind,
            });
          }
        };
        art.src = artworkUrl;
      };
      baseImg.onerror = () => {
        onChange?.({
          artwork_file_id: artworkFileId,
          zone, scale, rotation, position: pos,
          custom_text: customText || undefined,
          template_kind: podConfig.template_kind,
        });
      };
      baseImg.src = mockup;
    };
    compose();
  }, [artworkUrl, artworkFileId, pos, scale, rotation, customText, zone]);  // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="pod-customizer">
      {/* Upload */}
      {!artworkUrl ? (
        <label className="block border-2 border-dashed border-[#E5E3DD] rounded-sm p-8 text-center cursor-pointer hover:border-[#702A32] transition-colors" data-testid="pod-upload-zone">
          <input type="file" accept=".png,.jpg,.jpeg,.svg,.pdf" className="hidden" onChange={onFile} data-testid="pod-file-input" />
          <Upload className="w-8 h-8 mx-auto text-[#702A32] mb-3" strokeWidth={1.5} />
          <div className="font-medium">Upload your design</div>
          <div className="text-xs text-[#8A8782] mt-1">PNG · JPG · SVG · PDF · max 20MB</div>
        </label>
      ) : (
        <>
          {/* Mockup canvas */}
          <div
            ref={containerRef}
            className="relative aspect-square overflow-hidden surface select-none"
            onMouseMove={onDrag}
            onTouchMove={onDrag}
            onMouseLeave={endDrag}
            data-testid="pod-canvas"
          >
            <img src={mockup} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
            {/* Print zone overlay */}
            <div className="pod-zone absolute" style={{ inset: "20% 25%" }} />
            {/* Draggable artwork */}
            <div
              className="absolute cursor-move"
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${scale / 40})`,
                width: "32%",
              }}
              onMouseDown={startDrag}
              onTouchStart={startDrag}
              data-testid="pod-design"
            >
              <img src={artworkUrl} alt="design" className="w-full pointer-events-none" draggable={false} />
              {customText && podConfig.allow_custom_text && (
                <div className="text-center mt-2 font-medium text-[#1A1A18]">{customText}</div>
              )}
            </div>
          </div>
          <canvas ref={canvasRef} className="hidden" />

          {/* Controls */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Control icon={ZoomIn} label="Size" min={15} max={75} value={scale} onChange={setScale} testid="pod-scale" />
            <Control icon={RotateCw} label="Rotate" min={-180} max={180} value={rotation} onChange={setRotation} unit="°" testid="pod-rotate" />
            <button onClick={() => { setPos({ x: 50, y: 50 }); setRotation(0); setScale(40); }} className="btn-secondary text-sm inline-flex items-center justify-center gap-2" data-testid="pod-center-btn">
              <Move className="w-4 h-4" strokeWidth={1.5} /> Center
            </button>
          </div>

          {/* Zone */}
          {podConfig.placement_zones?.length > 1 && (
            <div>
              <div className="text-sm font-medium mb-2">Placement</div>
              <div className="flex flex-wrap gap-2">
                {podConfig.placement_zones.map((z) => (
                  <button
                    key={z}
                    onClick={() => setZone(z)}
                    className={`tag-pill border ${zone === z ? "bg-[#702A32] text-white border-[#702A32]" : "bg-white text-[#5C5A56] border-[#E5E3DD]"}`}
                    data-testid={`pod-zone-${z}`}
                  >
                    {z}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom text */}
          {podConfig.allow_custom_text && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Add custom text (optional)</label>
              <input className="input-field" value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="e.g. EST. 2026" data-testid="pod-custom-text" />
            </div>
          )}

          {warning && (
            <div className="flex items-start gap-2 p-3 bg-[#FFF4E0] border border-[#C78B1B]/40 text-[#8A6A14] text-sm" data-testid="pod-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.5} />
              <span>{warning}</span>
            </div>
          )}

          <button onClick={() => { setArtworkUrl(null); setArtworkFileId(null); setWarning(null); }} className="btn-ghost text-sm" data-testid="pod-clear">
            Upload a different design
          </button>
        </>
      )}
    </div>
  );
}

function Control({ icon: Icon, label, min, max, value, onChange, unit = "%", testid }) {
  return (
    <div className="surface p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}</span>
        <span className="font-mono text-xs">{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#702A32]" />
    </div>
  );
}
